/**
 * Sysadmin Library — parent layout (PR-J + Library-PR-X2 IA convergence).
 *
 * Top-level rail item /sysadmin/library — replaces the planned (and
 * dropped) Catalog → Knowledge sub-tab. Sysadmin authors system rows
 * (organization_id IS NULL) for folders, articles, tags, documents,
 * document folders, and sources, then publishes them; tenants see
 * published system content alongside their own.
 *
 * Sub-tabs (Library-PR-X2 — IA convergence to match tenant):
 *   /sysadmin/library/documents          — unified articles + uploaded docs
 *   /sysadmin/library/sources            — external connectors
 *   /sysadmin/library/tags               — tag taxonomy
 *
 * Bookmarks for the legacy entity-tabs (articles, folders,
 * document-folders) redirect to the unified Documents tab via thin
 * redirect routes — see library.articles.tsx, library.folders.tsx,
 * library.document-folders.tsx for the redirects.
 *
 * Why this layout
 * ---------------
 * The tenant Library uses intent-organized tabs (Favorites · Documents
 * · Sources · Quality · System) where Documents UNIFIES articles +
 * uploaded files + external. The pre-X2 sysadmin Library used 6
 * entity-organized tabs (one per backing table). Founder direction on
 * 2026-05-11 (`feedback_ui_parity_sysadmin_tenant.md`) is that same-
 * entity surfaces must look identical at both scopes; Documents is
 * the strongest case for unification because it's literally the same
 * tables on both sides (organization_id IS NULL vs NOT NULL). The
 * intent-views (Favorites, Quality, Shared-with-me) stay tenant-only —
 * they have no sysadmin analogue.
 *
 * Publish lifecycle for every entity: draft → published → deprecated → retired.
 * Bulk publish via the action bar inside each list page.
 */

import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { Tag, FileText, Cloud } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/library')({
  component: SysLibraryLayout,
});

const SUB_TABS: Array<{ key: string; label: string; icon: typeof Tag; to: string }> = [
  { key: 'documents', label: 'Documents', icon: FileText, to: '/sysadmin/library/documents' },
  { key: 'sources', label: 'Sources', icon: Cloud, to: '/sysadmin/library/sources' },
  { key: 'tags', label: 'Tags', icon: Tag, to: '/sysadmin/library/tags' },
];

function SysLibraryLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b sticky top-0 bg-background z-10">
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <div>
            <h1 className="text-lg font-semibold">System Library</h1>
            <p className="text-xs text-muted-foreground">
              Author system content (regulations, SOPs, platform docs). Publish to make visible to all tenants.
            </p>
          </div>
        </div>
        <nav className="flex items-center gap-0.5 overflow-x-auto px-6">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active =
              location.pathname === t.to || location.pathname.startsWith(t.to + '/');
            return (
              <Link
                key={t.key}
                to={t.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
